import { CaretDown } from '@creditornot/cb-icons';
import { typographyCss, cbSemanticCssVars, semanticColors, colors, elevationCss } from '@creditornot/cb-ingredients';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import React, { ReactNode, useCallback, useMemo } from 'react';
import {
  Theme as ReactSelectTheme,
  defaultTheme,
  ControlProps,
  OptionProps,
  MenuProps,
  MenuListComponentProps,
  PlaceholderProps,
  SingleValueProps,
} from 'react-select';
import Creatable from 'react-select/creatable';
import styled, { createGlobalStyle, css } from 'styled-components';

import { useThemeMode } from 'common/themeMode';

type Option = { value: string; label: string; disabled?: boolean };

type Props = {
  className?: string;
  options: Option[];
  value?: string | null | undefined;
  disabled: boolean;
  onChange?: (value: string | null) => void;
  formatCreateLabel?: (inputValue: string) => ReactNode;
};

const GlobalStyle = createGlobalStyle`
  .cb-select-Select__root {
    ${typographyCss.Body3()}
    color: ${cbSemanticCssVars.textPrimary};
  }
`;

const styles = {
  input: (base: any, { theme }: { theme: ReactSelectTheme }): any => ({
    ...base,
    marginLeft: 0,
    color: theme.colors.primary,
  }),
  singleValue: (base: any, { theme }: { theme: ReactSelectTheme }): any => ({
    ...base,
    marginLeft: 0,
    color: theme.colors.primary,
  }),
};

const useSelectTheme = (): ReactSelectTheme => {
  const { themeMode } = useThemeMode();

  const theme: ReactSelectTheme = useMemo(
    () => ({
      colors: {
        ...defaultTheme.colors,
        primary: semanticColors[themeMode].textPrimary,
      },
      borderRadius: 8,
      spacing: {
        /* Used to calculate consistent margin/padding on elements */
        baseUnit: 4,
        /* The minimum height of the control */
        controlHeight: 36,
        /* The amount of space between the control and menu */
        menuGutter: 4,
      },
    }),
    [themeMode],
  );

  return theme;
};

const ControlRoot = styled.div<{
  disabled: boolean;
  invalid: boolean;
  isFocused: boolean;
  open: boolean;
}>`
  position: relative;
  z-index: 0;

  display: flex;
  justify-content: space-between;
  align-items: stretch;
  outline: none;

  color: ${cbSemanticCssVars.textPrimary};
  min-height: 36px;
  min-width: 96px;
  flex-wrap: nowrap;
  box-sizing: border-box;
  box-shadow: none;
  border-radius: 8px;
  border-color: ${({ invalid, isFocused, open, theme: { cbSemanticColors = semanticColors.light } }) => {
    if (invalid) {
      return colors.strawberry100;
    }

    if (open || isFocused) {
      return colors.wolt100;
    }

    return cbSemanticColors.borderPrimary;
  }};
  border-width: 1px;
  border-style: solid;
  background-color: ${({ disabled, theme: { cbThemeMode = 'light' } }) => {
    if (cbThemeMode === 'light') {
      // ignore-color
      return disabled ? colors.pepper8 : colors.salt100;
    }

    // ignore-color
    return disabled ? colors.salt12 : 'transparent';
  }};
  transition:
    background-color 75ms ease-in,
    border-color 75ms ease-in;

  ${({ disabled, invalid, isFocused, open }) =>
    !disabled &&
    !invalid &&
    css`
      &:hover {
        border-color: ${({ theme: { cbThemeMode = 'light' } }) => {
          if (open || isFocused) {
            return colors.wolt100;
          }

          return cbThemeMode === 'light' ? colors.pepper20 : colors.salt20;
        }};
      }
    `}
`;

const Control = ({
  children,
  isDisabled,
  isFocused,
  innerProps,
  innerRef,
  selectProps,
}: ControlProps<Option, false>) => (
  <ControlRoot
    {...innerProps}
    disabled={isDisabled}
    isFocused={isFocused}
    invalid={selectProps['aria-invalid'] as boolean}
    open={!!selectProps.menuIsOpen}
    ref={innerRef}
  >
    {children}
  </ControlRoot>
);

const IndicatorsContainerRoot = styled.div`
  display: flex;
  align-self: stretch;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  box-sizing: border-box;
  min-width: 36px;
  height: 36px;
  padding-inline-end: 12px;

  > * + * {
    margin-inline-start: 4px;
  }
`;

const IndicatorsContainer = ({ children }: { children: ReactNode }) => (
  <IndicatorsContainerRoot>{children}</IndicatorsContainerRoot>
);

const StyledCaretDownIcon = styled(CaretDown)`
  width: 12px;
  height: 12px;
  fill: ${cbSemanticCssVars.textSecondary};
`;

const DropdownIndicator = () => <StyledCaretDownIcon />;

const ValueContainerRoot = styled.div`
  flex: 1;
  align-self: stretch;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
  color: ${cbSemanticCssVars.textPrimary};
  padding: 4px 12px;
  max-width: calc(100% - 52px);
`;

const ValueContainer = ({ children }: { children: ReactNode }) => <ValueContainerRoot>{children}</ValueContainerRoot>;

const Option = ({
  children,
  className,
  isDisabled,
  isFocused,
  isSelected,
  innerRef,
  innerProps,
}: OptionProps<Option, false>) => (
  <MenuActionItem
    className={className}
    disabled={isDisabled}
    focused={isFocused}
    selected={isSelected}
    id={innerProps.id}
    key={innerProps.key}
    onClick={innerProps.onClick as unknown as React.MouseEventHandler<HTMLButtonElement>}
    onMouseMove={innerProps.onMouseMove as unknown as React.MouseEventHandler<HTMLButtonElement>}
    onMouseOver={innerProps.onMouseOver as unknown as React.MouseEventHandler<HTMLButtonElement>}
    ref={innerRef as (instance: HTMLButtonElement | null) => void}
    tabIndex={innerProps.tabIndex}
    variant="black"
  >
    {children}
  </MenuActionItem>
);

const MenuRoot = styled.div<{
  bottom?: string;
  maxMenuHeight: number;
  minMenuHeight: number;
  top?: string;
}>`
  position: absolute;
  z-index: 1;
  bottom: ${({ bottom }) => bottom || 'auto'};
  top: ${({ top }) => top || 'auto'};
  left: 0;
  right: 0;
  max-width: 100%;
  margin-block-end: 4px;
  margin-block-start: 4px;
  border-radius: 8px;
  box-sizing: border-box;
  ${elevationCss(8)};
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  min-height: ${({ minMenuHeight }) => minMenuHeight}px;
  max-height: ${({ maxMenuHeight }) => maxMenuHeight}px;
  overflow-y: hidden;
`;

const MenuScroller = styled.div<{
  maxMenuHeight: number;
  minMenuHeight: number;
}>`
  height: 100%;
  min-height: ${({ minMenuHeight }) => minMenuHeight}px;
  max-height: ${({ maxMenuHeight }) => maxMenuHeight}px;
  overflow-y: auto;
`;

const Menu = (props: MenuProps<Option, false>) => {
  const styles = props.getStyles('menu', props) as { [key: string]: string };

  return (
    <MenuRoot
      {...props.innerProps}
      bottom={styles.bottom}
      maxMenuHeight={props.maxMenuHeight}
      minMenuHeight={props.minMenuHeight}
      ref={props.innerRef}
      top={styles.top}
    >
      <MenuScroller maxMenuHeight={props.maxMenuHeight} minMenuHeight={props.minMenuHeight}>
        {props.children}
      </MenuScroller>
    </MenuRoot>
  );
};

const MenuListRoot = styled.div`
  padding: 8px 16px;
`;

const MenuList = ({ children }: MenuListComponentProps<Option, false>) => (
  <MenuListRoot>
    <MenuItemGroup>{children}</MenuItemGroup>
  </MenuListRoot>
);

const PlaceholderRoot = styled.div`
  position: absolute;
  top: 0;
  inset-inline-start: 12px;
  bottom: 0;
  color: ${cbSemanticCssVars.textSecondary};

  display: flex;
  align-items: center;
`;

const Placeholder = ({ children }: PlaceholderProps<Option, false>) => <PlaceholderRoot>{children}</PlaceholderRoot>;

const SingleValueRoot = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 44px);
`;

const SingleValue = ({ children }: SingleValueProps<Option>) => <SingleValueRoot>{children}</SingleValueRoot>;

const components = {
  Control,
  IndicatorSeparator: null,
  IndicatorsContainer,
  DropdownIndicator,
  ValueContainer,
  Option,
  Menu,
  MenuList,
  Placeholder,
  SingleValue,
};

const CreatableSelect = ({ className, options, value, disabled, onChange, formatCreateLabel }: Props) => {
  const theme = useSelectTheme();

  const selectedOption = useMemo(() => {
    const existingOption = options.find(opt => opt.value === value);
    if (existingOption) {
      return existingOption;
    }
    return value ? { value, label: value } : undefined;
  }, [options, value]);

  const handleChange = useCallback(
    (option: Option) => {
      if (onChange) {
        onChange(option ? option.value : null);
      }
    },
    [onChange],
  );

  return (
    <>
      <Creatable
        autoFocus
        formatCreateLabel={formatCreateLabel}
        className={`cb-select-Select__root ${className || ''}`}
        components={components}
        isDisabled={disabled}
        isSearchable
        maxMenuHeight={300}
        menuPlacement="bottom"
        minMenuHeight={48}
        onChange={handleChange}
        options={options}
        placeholder="Select..."
        styles={styles}
        theme={theme}
        value={selectedOption}
        isOptionDisabled={option => !!option.disabled}
      />

      <GlobalStyle />
    </>
  );
};

export default CreatableSelect;
